import styled, { useTheme } from 'styled-components';
import {
  MdOutlineKeyboardArrowLeft,
  MdOutlineKeyboardArrowRight,
  MdOutlineKeyboardDoubleArrowLeft,
  MdOutlineKeyboardDoubleArrowRight,
} from 'react-icons/md';
import { updateHashUrl, parseHashParams } from '../utils/hashRouter';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 50px;
  margin-bottom: 80px;
  user-select: none;
`;

const PageItem = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 40px;
  background-color: ${(props) =>
    props.$isActive
      ? props.theme.colors.primaryText
      : props.theme.colors.white};
  color: ${(props) =>
    props.$isActive
      ? props.theme.colors.white
      : props.theme.colors.primaryText};
  font-size: 14px;
  font-weight: ${(props) => (props.$isActive ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid
    ${(props) =>
      props.$isActive
        ? props.theme.colors.primaryText
        : props.theme.colors.border};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      !props.$isActive && props.theme.colors.primaryText};
    border-color: ${(props) => props.theme.colors.primaryText};
    color: ${(props) => props.theme.colors.white};
  }

  &:disabled {
    background-color: ${(props) => props.theme.colors.disabledBackground};
    color: ${(props) => props.theme.colors.disabledText};
    border-color: ${(props) => props.theme.colors.border};
    cursor: not-allowed;
  }
`;

const ArrowButton = styled(PageItem)`
  width: 40px;
  height: 40px;
  background-color: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.border};

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.primaryText};
    border-color: ${(props) => props.theme.colors.primaryText};
    color: ${(props) => props.theme.colors.white};
  }
`;

const Pagination = ({ totalPages, currentPage, setCurrentPage }) => {
  const theme = useTheme();
  const renderPages = 5;
  const pageGroup = Math.ceil(currentPage / renderPages);
  const startPage = (pageGroup - 1) * renderPages + 1;
  const endPage = Math.min(pageGroup * renderPages, totalPages);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Parse current hash params and update with new page
    const { params } = parseHashParams();
    const currentParams = {
      category: params.get('category'),
      brand: params.get('brand'),
      subcategory: params.get('subcategory'),
      keyword: params.get('keyword'),
      filter: params.get('filter'),
    };
    updateHashUrl('/search', {
      ...currentParams,
      page: newPage,
    });
    scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevGroup = () => {
    // const prevPage = startPage - 1;
    handlePageChange(1);
  };

  const handleNextGroup = () => {
    // const nextPage = endPage + 1;
    handlePageChange(totalPages);
  };

  return (
    <PaginationContainer>
      <ArrowButton onClick={handlePrevGroup} disabled={startPage === 1}>
        <MdOutlineKeyboardDoubleArrowLeft
          size={20}
          color={theme.colors.secondaryText}
        />
      </ArrowButton>
      <ArrowButton
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <MdOutlineKeyboardArrowLeft
          size={20}
          color={theme.colors.secondaryText}
        />
      </ArrowButton>

      {Array.from({ length: endPage - startPage + 1 }, (_, i) => (
        <PageItem
          key={startPage + i}
          $isActive={currentPage === startPage + i}
          onClick={() => handlePageChange(startPage + i)}
        >
          {startPage + i}
        </PageItem>
      ))}

      <ArrowButton
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <MdOutlineKeyboardArrowRight
          size={20}
          color={theme.colors.secondaryText}
        />
      </ArrowButton>
      <ArrowButton onClick={handleNextGroup} disabled={endPage === totalPages}>
        <MdOutlineKeyboardDoubleArrowRight
          size={20}
          color={theme.colors.secondaryText}
        />
      </ArrowButton>
    </PaginationContainer>
  );
};

export default Pagination;
